import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Store,
  LocationOn,
  Phone,
  CheckCircle,
  LocalShipping,
  ShoppingBag,
  Restaurant,
  Close,
  Refresh,
} from '@mui/icons-material';
import { useRestaurant } from '../../contexts/RestaurantContext';

const OutletSelectionModal = ({ open, onClose, onOutletSelect }) => {
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // Debug modal props
  console.log('OutletSelectionModal - open:', open);
  console.log('OutletSelectionModal - onClose:', onClose);
  console.log('OutletSelectionModal - onOutletSelect:', onOutletSelect);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { 
    outlets, 
    loadOutletsByService, 
    outletsLoading,
    outletsLoaded,
    outletsLoadingAttempted,
    isDeliveryAvailable,
    isTakeawayAvailable,
    isDineInAvailable
  } = useRestaurant();

  // Only load outlets once when modal opens and we haven't loaded them yet
  useEffect(() => {
    if (open && !hasLoaded && !outletsLoading && !outletsLoadingAttempted) {
      loadAllOutlets();
    }
  }, [open, hasLoaded, outletsLoading, outletsLoadingAttempted]);

  // Reset loading state when modal closes
  useEffect(() => {
    if (!open) {
      setHasLoaded(false);
      setError(null);
    }
  }, [open]);

  const loadAllOutlets = async () => {
    if (hasLoaded || outletsLoading || outletsLoadingAttempted) {
      return; // Prevent multiple calls
    }
    
    try {
      setLoading(true);
      setError(null);
      setHasLoaded(true);
      
      // Load outlets for all service types
      const [deliveryOutlets, takeawayOutlets, dineInOutlets] = await Promise.all([
        loadOutletsByService('delivery'),
        loadOutletsByService('takeaway'),
        loadOutletsByService('dine_in')
      ]);
      
      // Combine and deduplicate outlets
      const allOutlets = [...deliveryOutlets, ...takeawayOutlets, ...dineInOutlets];
      const uniqueOutlets = allOutlets.filter((outlet, index, self) => 
        index === self.findIndex(o => o.id === outlet.id)
      );
      
    } catch (err) {
      console.error('Error loading outlets:', err);
      setError('Failed to load outlets. Please try again.');
      setHasLoaded(false); // Allow retry
    } finally {
      setLoading(false);
    }
  };

  const handleOutletSelect = (outlet) => {
    setSelectedOutlet(outlet);
  };

  const handleConfirmSelection = () => {
    if (selectedOutlet) {
      onOutletSelect(selectedOutlet);
      onClose();
    }
  };

  const getOutletServices = (outlet) => {
    const services = [];
    if (outlet.is_delivery_available || outlet.delivery_enabled) services.push('Delivery');
    if (outlet.is_takeaway_available || outlet.takeaway_enabled) services.push('Takeaway');
    if (outlet.is_dine_in_available || outlet.dine_in_enabled) services.push('Dine-in');
    
    // If no specific services are defined, assume all are available
    if (services.length === 0) {
      services.push('Delivery', 'Takeaway', 'Dine-in');
    }
    
    return services;
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'Delivery': return <LocalShipping />;
      case 'Takeaway': return <ShoppingBag />;
      case 'Dine-in': return <Restaurant />;
      default: return <Store />;
    }
  };

  const getServiceColor = (service) => {
    switch (service) {
      case 'Delivery': return 'primary';
      case 'Takeaway': return 'secondary';
      case 'Dine-in': return 'success';
      default: return 'default';
    }
  };

  console.log('OutletSelectionModal - Rendering modal with open:', open);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        zIndex: 9999,
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100vh' : '60vh',
          zIndex: 9999,
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Store color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
              Select Your Location
            </Typography>
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
              Choose your preferred outlet to continue
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
          <Close sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={loadAllOutlets}
                startIcon={<Refresh />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8,
            gap: 2
          }}>
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary">
              Loading outlets...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Available Outlets ({outlets.length})
            </Typography>
            
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {outlets.map((outlet) => {
                const services = getOutletServices(outlet);
                const isSelected = selectedOutlet?.id === outlet.id;
                
                return (
                  <Grid item xs={12} sm={6} key={outlet.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: isSelected ? 'primary.100' : 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        }
                      }}
                      onClick={() => handleOutletSelect(outlet)}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          justifyContent: 'space-between',
                          mb: { xs: 1.5, sm: 2 }
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                              wordBreak: 'break-word'
                            }}>
                              {outlet.name}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                              <LocationOn sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary', mt: 0.25 }} />
                              <Typography variant="body2" color="text.secondary" sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                wordBreak: 'break-word',
                                lineHeight: 1.4
                              }}>
                                {outlet.address}
                              </Typography>
                            </Box>
                            
                            {outlet.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, sm: 2 } }}>
                                <Phone sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}>
                                  {outlet.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          {isSelected && (
                            <CheckCircle 
                              color="primary" 
                              sx={{ 
                                fontSize: { xs: 20, sm: 24 },
                                ml: 1,
                                flexShrink: 0
                              }} 
                            />
                          )}
                        </Box>
                        
                        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
                        
                        <Box>
                          <Typography variant="body2" fontWeight="600" gutterBottom sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            mb: 1
                          }}>
                            Available Services:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {services.map((service) => (
                              <Chip
                                key={service}
                                icon={getServiceIcon(service)}
                                label={service}
                                color={getServiceColor(service)}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                  height: { xs: '20px', sm: '24px', md: '28px' },
                                  '& .MuiChip-icon': {
                                    fontSize: { xs: '14px', sm: '16px' }
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            
            {outlets.length === 0 && !loading && (hasLoaded || outletsLoadingAttempted) && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Store sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Outlets Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  We're sorry, but there are currently no outlets available in your area.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={loadAllOutlets}
                  startIcon={<Refresh />}
                  sx={{ mt: 2 }}
                >
                  Try Again
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, sm: 3 },
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: { xs: 1, sm: 2 },
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
          sx={{ 
            minWidth: { xs: 'auto', sm: '120px' },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            py: { xs: 1.5, sm: 1.25 }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
          disabled={!selectedOutlet}
          startIcon={selectedOutlet ? <CheckCircle /> : null}
          sx={{ 
            minWidth: { xs: 'auto', sm: '140px' },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            py: { xs: 1.5, sm: 1.25 }
          }}
        >
          {selectedOutlet ? 'Continue' : 'Select Outlet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutletSelectionModal;